import type { FC } from 'react';
import type { CoBuyer, IShopper } from '@makemydeal/dr-platform-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useDispatch, useSelector } from 'react-redux';

import { Alert } from '@interstate/components/Alert';
import { Box } from '@interstate/components/Box';
import { Button } from '@interstate/components/Button';
import { ArrowsRightLeftIcon } from '@interstate/components/Icons/ArrowsRightLeftIcon';
import { Tooltip } from '@interstate/components/Tooltip';

import { BreakPoint } from '@makemydeal/dr-activities-common';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as editCustomerActionCreators from '@makemydeal/dr-dash-store/actions/editCustomerActionCreators';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as contractingActionCreators from '@makemydeal/dr-dash-store/actions/contractingActionCreators';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import * as customerSpdActionCreators from '@makemydeal/dr-dash-store/actions/CustomerSpdActionCreators';
import * as editCustomerSelectors from '@makemydeal/dr-dash-store/selectors/editCustomerSelectors';
import * as customerSpdSelectors from '@makemydeal/dr-dash-store/selectors/customerSpdSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as contractingSelectors from '@makemydeal/dr-dash-store/selectors/contractingSelectors';
import * as dealModeSelectors from '@makemydeal/dr-dash-store/selectors/dealModeSelectors';
import * as dealXgDetailsSelectors from '@makemydeal/dr-dash-store/selectors/dealXgDetailsSelectors';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import { createInsuranceAnalyticsHandler } from '../vehicleInsurance/insuranceAnalyticsHandler';

import { dealLockHooks } from '../hooks';
import {
    getFormValues,
    mapFormValuesToCustomerPayload,
    type CustomerCardFormValues
} from '../customerCard/utils/CustomerCardUtils';
import {
    CONVERT_TO_BUSINESS_SOLD_CONTRACTED_ALERT,
    CONVERT_TO_BUSINESS_SOLD_CONTRACTED_ALERT_TITLE,
    CUSTOMER_INFORMATION_HEADER
} from '../customerCard/constants';
import type { INSURANCE } from '../customerCard/constants';
import { LoadingSection, LoadingSectionParent } from '@makemydeal/dr-shared-components';
import UnifiedCustomerCard, { type UnifiedCustomerCardRef } from '../unifiedCustomerCard/UnifiedCustomerCard';
import {
    UnifiedCustomerCard as ActivitiesUnifiedCustomerCard,
    CUSTOMER_CARD_ANALYTICS,
    CARD_OPERATION_STEP,
    CONTRACT_GENERATED_UNAVAILABLE_REASON,
    setCustomerCardVariant
} from '@makemydeal/dr-activities-customer-card';
import type {
    CustomerCardHostState,
    CustomerCardSaveData,
    UnifiedCustomerCardRef as ActivitiesUnifiedCustomerCardRef,
    CustomerCardAnalyticsEvent,
    CustomerTabKey
} from '@makemydeal/dr-activities-customer-card';
import { StyledEditCustomerSlideOut } from './EditCustomer.styles';
import BusinessSearch from './BusinessSearch';
import { ChevronLeftIcon } from '@interstate/components/Icons';

const CONVERT_TO_BUSINESS_TITLE = 'Convert to Business';

const EditCustomerSlideout: FC = () => {
    const dispatch = useDispatch();

    // Selectors
    const isActivitiesCustomerCardEnabled = useSelector(featureToggleSelectors.isActivitiesCustomerCardEnabled);
    const isSpdManagementEnabled = useSelector(dealerSelectors.isSpdManagementEnabled);
    const isPostalCodeDigitsOnlyValidationEnabled = useSelector(featureToggleSelectors.isPostalCodeDigitsOnlyValidationEnabled);
    const activeCustomerTab = useSelector(editCustomerSelectors.showEditCustomerSlideout);
    const isSpdCheckLoading = useSelector(customerSpdSelectors.getCheckCustomerSpdLoading);
    const buyer = useSelector(offerReduxSelectors.getShopperInfo);
    const coBuyerInfo = useSelector(offerReduxSelectors.getCoBuyerInfo);
    const dealershipId = useSelector(offerReduxSelectors.getCommonOrgId);
    const isOptionalDesking = useSelector(dealModeSelectors.isOptionalDeskingMode);
    const isDealSoldOrContracted = useSelector(editCustomerSelectors.getIsDealSoldOrContracted);
    const isContractingContext = useSelector(contractingSelectors.getIsContractingContext);
    const isContractGenerated = useSelector(contractingSelectors.getIsContractGenerated);
    const isChangeBuyerCoBuyerEnabled = useSelector(featureToggleSelectors.isChangeBuyerCoBuyerEnabled);
    const isLocked = dealLockHooks.useIsDealLocked();
    const showBusinessSearch = useSelector(editCustomerSelectors.getShowBusinessSearch);
    const selectedBusinessEntityId = useSelector(editCustomerSelectors.getSelectedBusinessEntityId);
    const businessSearchResults = useSelector(editCustomerSelectors.getBusinessSearchResults);
    const isCreditAppFieldsCoverageEnabled = useSelector(featureToggleSelectors.isCreditAppFieldsCoverageEnabled);
    const enableCorrectPhoneHandling = useSelector(featureToggleSelectors.isCorrectPhoneHandlingEnabled);
    const isNativeAddCustomerEnabled = useSelector(featureToggleSelectors.isNativeAddCustomerEnabled);
    const dealxgId = useSelector(offerReduxSelectors.getDealXgId);
    const isEcommerceDeal = useSelector(dealXgDetailsSelectors.getIsEcommerceDeal);
    const isEcommerceCancelOnBuyerEmailChangeEnabled = useSelector(
        featureToggleSelectors.isEcommerceCancelOnBuyerEmailChangeEnabled
    );

    // Responsive width
    const isSmallScreen = useMediaQuery({ query: `(max-width: ${BreakPoint.SMALL})` });
    const isLargeScreen = useMediaQuery({ query: `(min-width: ${BreakPoint.MEDIUM})` });
    const newPanelWidth = isLargeScreen ? '956px' : '100%';
    const panelWidth = isSmallScreen ? '100%' : '756px';

    // Refs
    const activitiesUnifiedRef = useRef<ActivitiesUnifiedCustomerCardRef>(null);
    const unifiedRef = useRef<UnifiedCustomerCardRef>(null);

    // State
    const [shellState, setShellState] = useState({ canApply: false, canCancel: false, isApplying: false });
    const [activitiesShellState, setActivitiesShellState] = useState<CustomerCardHostState>({
        canApply: false,
        canCancel: true,
        isSaving: false
    });
    const [buyerBusinessConversionDraft, setBuyerBusinessConversionDraft] = useState<CustomerCardFormValues | null>(null);
    const [showSoldContractedAlert, setShowSoldContractedAlert] = useState(false);

    // Remove the alert when an operation starts
    useEffect(() => {
        if (activitiesShellState.operation) setShowSoldContractedAlert(false);
    }, [activitiesShellState.operation]);

    // Reports which of the two mutually-exclusive card variants is live, for NR instrumentation.
    // Read here (not at bootstrap) because feature toggles aren't safely readable until after
    // BOOTSTRAP_SUCCESS's reducer runs.
    useEffect(() => {
        setCustomerCardVariant(isActivitiesCustomerCardEnabled ? 'activities' : 'unified');
    }, [isActivitiesCustomerCardEnabled]);

    const isBuyerBusinessConversionFlowActive = !!buyerBusinessConversionDraft;

    // Convert-to-Business CTA visibility
    const showConvertToBusinessCta =
        buyer?.customerType !== 'Business' &&
        !isBuyerBusinessConversionFlowActive &&
        !showBusinessSearch &&
        (!isActivitiesCustomerCardEnabled || !activitiesShellState.operation);
    const isConvertDisabledByContract = isChangeBuyerCoBuyerEnabled && isContractGenerated;

    // Handlers
    const resetBuyerBusinessConversionDraft = () => {
        setBuyerBusinessConversionDraft(null);
        dispatch(editCustomerActionCreators.clearCreateBusinessError());
        dispatch(editCustomerActionCreators.clearAssociateBusinessError());
    };

    const handleConvertToBusinessClick = () => {
        if (isDealSoldOrContracted) {
            setShowSoldContractedAlert(true);
            return;
        }
        if (isActivitiesCustomerCardEnabled) {
            activitiesUnifiedRef.current?.convertToBusiness?.();
            return;
        }
        dispatch(editCustomerActionCreators.showBusinessSearch());
    };

    const handleAddNewBusiness = (searchText: string) => {
        dispatch(editCustomerActionCreators.hideBusinessSearch());
        dispatch(editCustomerActionCreators.showEditBuyerSlideout());
        const baseBuyerFormValues = getFormValues(buyer, {}, enableCorrectPhoneHandling);
        setBuyerBusinessConversionDraft({
            ...baseBuyerFormValues,
            business: {
                legalName: searchText.trim(),
                businessEntityId: ''
            }
        });
    };

    const handleExistingBusinessSelect = () => {
        dispatch(editCustomerActionCreators.hideBusinessSearch());
        const baseBuyerFormValues = getFormValues(buyer, {}, enableCorrectPhoneHandling);
        const selectedBusiness = businessSearchResults.find((b) => b.businessEntityId === selectedBusinessEntityId);
        const businessAddress = selectedBusiness?.primaryAddress;

        // Only fall back to the business's address/phone when the buyer record has none of its own --
        // an existing buyer value always wins so manual entry is never overwritten.
        const hasBuyerAddress = !!(
            baseBuyerFormValues.streetAddress ||
            baseBuyerFormValues.city ||
            baseBuyerFormValues.state ||
            baseBuyerFormValues.zip
        );
        const hasBuyerPhone = !!baseBuyerFormValues.phone;

        setBuyerBusinessConversionDraft({
            ...baseBuyerFormValues,
            ...(!hasBuyerAddress &&
                businessAddress && {
                    streetAddress: businessAddress.street1 ?? '',
                    streetAddress2: businessAddress.street2 ?? '',
                    city: businessAddress.city ?? '',
                    state: businessAddress.stateOrProvinceAbbreviation ?? businessAddress.stateOrProvince ?? '',
                    zip: businessAddress.postalCode ?? '',
                    county: businessAddress.countyOrRegion ?? ''
                }),
            ...(!hasBuyerPhone && selectedBusiness?.primaryPhone && { phone: selectedBusiness.primaryPhone }),
            business: {
                legalName: selectedBusiness?.businessName || '',
                businessEntityId: selectedBusinessEntityId as string
            }
        });
    };

    const handleBackFromBusinessSearch = () => {
        dispatch(editCustomerActionCreators.hideBusinessSearch());
    };

    const handleShellCancel = () => {
        // Short-circuit for business search mode
        if (showBusinessSearch) {
            handleBackFromBusinessSearch();
            return;
        }

        setShowSoldContractedAlert(false);

        // Route by branch
        if (isActivitiesCustomerCardEnabled) {
            activitiesUnifiedRef.current?.cancel();
        } else {
            unifiedRef.current?.cancel();
        }
    };

    const handleShellApply = () => {
        if (isActivitiesCustomerCardEnabled) {
            activitiesUnifiedRef.current?.apply();
        } else {
            setShowSoldContractedAlert(false);
            unifiedRef.current?.apply();
        }
    };

    const closeCustomerSlideOut = () => {
        resetBuyerBusinessConversionDraft();
        setShowSoldContractedAlert(false);
        dispatch(editCustomerActionCreators.hideEditCustomerSlideout());
    };

    const handleActivitiesSave = ({
        buyer: updatedBuyer,
        coBuyer,
        replaced,
        insurance,
        spdValues,
        allSaved
    }: CustomerCardSaveData) => {
        if (updatedBuyer) {
            const allowEmptyValues = true;
            const buyerPayload = mapFormValuesToCustomerPayload(
                updatedBuyer as CustomerCardFormValues,
                updatedBuyer.commonConsumerId ?? '',
                dealershipId,
                replaced?.buyer ? (updatedBuyer as unknown as IShopper) : buyer,
                enableCorrectPhoneHandling
            );
            const buyerDateOfBirth = spdValues?.[updatedBuyer.commonConsumerId ?? '']?.dateOfBirth;
            if (buyerDateOfBirth) {
                buyerPayload.dateOfBirth = buyerDateOfBirth;
            }
            dispatch(
                offerReduxActionCreators.updatedShopperLocationFromConsumer(
                    buyer,
                    buyerPayload,
                    allowEmptyValues,
                    isOptionalDesking,
                    replaced?.buyer,
                    isPostalCodeDigitsOnlyValidationEnabled
                )
            );
            if (coBuyer) {
                const coBuyerPayload = mapFormValuesToCustomerPayload(
                    coBuyer as CustomerCardFormValues,
                    coBuyer.commonConsumerId ?? '',
                    dealershipId,
                    replaced?.coBuyer ? (coBuyer as CoBuyer) : coBuyerInfo ?? {},
                    enableCorrectPhoneHandling
                );
                const coBuyerDateOfBirth = spdValues?.[coBuyer.commonConsumerId ?? '']?.dateOfBirth;
                if (coBuyerDateOfBirth) {
                    coBuyerPayload.dateOfBirth = coBuyerDateOfBirth;
                }
                dispatch(
                    offerReduxActionCreators.updateCoBuyer(
                        coBuyerPayload,
                        allowEmptyValues,
                        isOptionalDesking,
                        replaced?.coBuyer,
                        isPostalCodeDigitsOnlyValidationEnabled
                    )
                );
            }
        }
        if (insurance) {
            dispatch(baseDealActionCreators.saveVehicleInsuranceInfo(insurance));
        }
        if (spdValues) {
            Object.entries(spdValues).forEach(([commonConsumerId, values]) => {
                dispatch(customerSpdActionCreators.updateCustomerSpd(commonConsumerId, values));
            });
        }
        if (isContractingContext) {
            // Reset the red dots of the saved entities, then refresh contract validation to set them again
            // from the saved data. Clearing after the refresh would wipe the errors it just found.
            // clearEntityErrors is required because setErrorFields merges and cannot remove stale errors.
            // SPD-only saves omit updatedBuyer/coBuyer, so they are detected by the current ccid.
            const buyerSpdSaved = Boolean(spdValues?.[buyer?.commonConsumerId ?? '']);
            const coBuyerSpdSaved = Boolean(spdValues?.[coBuyerInfo?.commonConsumerId ?? '']);
            if (updatedBuyer || buyerSpdSaved) {
                dispatch(dealScreenErrorActionCreators.clearEntityErrors('buyer'));
            }
            if (coBuyer || coBuyerSpdSaved) {
                dispatch(dealScreenErrorActionCreators.clearEntityErrors('coBuyer'));
            }
            if (insurance) {
                dispatch(dealScreenErrorActionCreators.clearEntityErrors('vehicleInsurance'));
            }
            dispatch(contractingActionCreators.refreshContractValidationErrors());
        }
        // The credit-consent banner renders inside the card, so closing on save would unmount it the
        // instant it appears. Stay open only for the save that *grants* consent, compared against the
        // pre-save store value — the saved value stays true forever, so testing it directly would stop
        // the slideout ever closing for an already-consented customer.
        const buyerGrantedConsent = Boolean(updatedBuyer?.creditPullConsentGiven) && !buyer?.creditPullConsentGiven;
        const coBuyerGrantedConsent = Boolean(coBuyer?.creditPullConsentGiven) && !coBuyerInfo?.creditPullConsentGiven;
        if (allSaved && !buyerGrantedConsent && !coBuyerGrantedConsent) {
            closeCustomerSlideOut();
        }
    };

    const insuranceAnalyticsHandler = useMemo(() => createInsuranceAnalyticsHandler(dispatch), [dispatch]);

    const handleBusinessConversionComplete = (data: { commonConsumerId: string; businessEntityId: string }) => {
        dispatch(offerReduxActionCreators.businessConversionCompleted(data.commonConsumerId));
    };

    // The activities card emits neutral analytics event names; the host maps each to its own dispatch.
    // Keeping the dash action here (not inside the activity) lets each host react its own way.
    const handleActivitiesAnalyticsEvent = (eventName: CustomerCardAnalyticsEvent) => {
        switch (eventName) {
            case CUSTOMER_CARD_ANALYTICS.CLICKED_SHOW_CUSTOMER_SPD:
                dispatch(customerSpdActionCreators.clickedShowCustomerSpd());
                break;
            default:
                insuranceAnalyticsHandler(eventName);
                break;
        }
    };

    // Apply/Cancel enable state
    const applyDisabled = isActivitiesCustomerCardEnabled ? !activitiesShellState.canApply : !shellState.canApply;

    const cancelDisabled = isActivitiesCustomerCardEnabled ? !activitiesShellState.canCancel : !shellState.canCancel;

    // Header with Convert-to-Business CTA
    const activitiesOperationStep = activitiesShellState.operation?.step ?? null;
    const isActivitiesSearchStep = isActivitiesCustomerCardEnabled && activitiesOperationStep === CARD_OPERATION_STEP.SEARCH;
    const isActivitiesEditStep = isActivitiesCustomerCardEnabled && activitiesOperationStep === CARD_OPERATION_STEP.EDIT;
    const isActivitiesOperationRunning = isActivitiesSearchStep || isActivitiesEditStep;
    const canGoBackFromOperation = isActivitiesOperationRunning && activitiesShellState.canGoBack !== false;
    const showOperationBackButton = canGoBackFromOperation || showBusinessSearch;
    const convertToBusinessButton = (
        <Button
            id="customer-slideout-convert-to-business-btn"
            data-testid="customer-slideout-convert-to-business-button"
            buttonStyle="tertiary"
            size="small"
            startIcon={<ArrowsRightLeftIcon />}
            disabled={isLocked || isConvertDisabledByContract}
            onClick={handleConvertToBusinessClick}
            sx={{ marginLeft: 'auto' }}
        >
            Convert to Business
        </Button>
    );
    const headerContent =
        isActivitiesOperationRunning || showBusinessSearch ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                {showOperationBackButton && (
                    <Button
                        data-testid="business-search-back-button"
                        buttonStyle="tertiary"
                        size="small"
                        startIcon={<ChevronLeftIcon />}
                        onClick={
                            canGoBackFromOperation ? () => activitiesUnifiedRef.current?.goBack?.() : handleBackFromBusinessSearch
                        }
                        sx={{ padding: 0 }}
                    >
                        Back
                    </Button>
                )}
                <span>{isActivitiesOperationRunning ? activitiesShellState.operationTitle : CONVERT_TO_BUSINESS_TITLE}</span>
            </Box>
        ) : showConvertToBusinessCta ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <span>{CUSTOMER_INFORMATION_HEADER}</span>
                {isConvertDisabledByContract ? (
                    <Tooltip toolTipContent={CONTRACT_GENERATED_UNAVAILABLE_REASON} position="bottom" size="large">
                        <span data-testid="customer-slideout-convert-to-business-disabled-tooltip">{convertToBusinessButton}</span>
                    </Tooltip>
                ) : (
                    convertToBusinessButton
                )}
            </Box>
        ) : (
            CUSTOMER_INFORMATION_HEADER
        );

    return (
        <StyledEditCustomerSlideOut
            data-testid="customer-slide-out"
            id="customer-slide-out"
            show={!!activeCustomerTab}
            position="right"
            panelWidth={isCreditAppFieldsCoverageEnabled ? newPanelWidth : panelWidth}
            onHide={() => {
                if (isActivitiesCustomerCardEnabled) {
                    if (isActivitiesEditStep) {
                        activitiesUnifiedRef.current?.cancel();
                        return;
                    }
                    if (isActivitiesSearchStep) {
                        if (canGoBackFromOperation) {
                            activitiesUnifiedRef.current?.cancelOperation?.();
                        } else {
                            activitiesUnifiedRef.current?.cancel();
                        }
                        return;
                    }
                }
                handleShellCancel();
            }}
            header={headerContent}
            {...(!isSpdCheckLoading &&
                !showBusinessSearch &&
                !isActivitiesSearchStep && {
                    footer: {
                        layout: 'rightAligned',
                        ...(activitiesShellState.hasRenderError
                            ? {
                                  primary: {
                                      action: handleShellCancel,
                                      label: 'Cancel',
                                      customIdentifiers: {
                                          primaryButton: {
                                              dataTestId: 'customer-slideout-cancel-button'
                                          }
                                      }
                                  }
                              }
                            : {
                                  options: [
                                      {
                                          action: handleShellCancel,
                                          label: 'Cancel',
                                          buttonStyle: 'tertiary',
                                          disabled: cancelDisabled,
                                          customIdentifiers: {
                                              optionButton0: {
                                                  dataTestId: 'customer-slideout-cancel-button'
                                              }
                                          }
                                      }
                                  ],
                                  primary: {
                                      action: handleShellApply,
                                      label: isActivitiesCustomerCardEnabled ? 'Save' : 'Apply',
                                      disabled: applyDisabled,
                                      customIdentifiers: {
                                          primaryButton: {
                                              dataTestId: 'customer-slideout-apply-button'
                                          }
                                      }
                                  }
                              })
                    }
                })}
            {...((isActivitiesSearchStep || showBusinessSearch) && {
                footer: {
                    layout: 'rightAligned',
                    // With no Back control the search step would have no way out but the close icon.
                    ...(isActivitiesSearchStep &&
                        !canGoBackFromOperation && {
                            options: [
                                {
                                    action: () => activitiesUnifiedRef.current?.cancel(),
                                    label: 'Cancel',
                                    buttonStyle: 'tertiary' as const,
                                    customIdentifiers: {
                                        optionButton0: {
                                            dataTestId: 'customer-slideout-cancel-button'
                                        }
                                    }
                                }
                            ]
                        }),
                    primary: {
                        action: isActivitiesSearchStep
                            ? () => activitiesUnifiedRef.current?.continueOperation?.()
                            : handleExistingBusinessSelect,
                        label: 'Continue',
                        disabled: isActivitiesSearchStep ? !activitiesShellState.canContinue : !selectedBusinessEntityId,
                        customIdentifiers: {
                            primaryButton: {
                                dataTestId: 'business-search-continue-button'
                            }
                        }
                    }
                }
            })}
        >
            {showSoldContractedAlert && (
                <Alert
                    type="info"
                    title={CONVERT_TO_BUSINESS_SOLD_CONTRACTED_ALERT_TITLE}
                    sx={{ margin: '1rem 0' }}
                    data-testid="convert-to-business-sold-contracted-alert"
                >
                    {CONVERT_TO_BUSINESS_SOLD_CONTRACTED_ALERT}
                </Alert>
            )}
            {showBusinessSearch ? (
                <BusinessSearch handleNewBusiness={handleAddNewBusiness} />
            ) : isActivitiesCustomerCardEnabled ? (
                <LoadingSectionParent>
                    <LoadingSection loading={activitiesShellState.isSaving} testId="activities-customer-card-loading" />
                    <ActivitiesUnifiedCustomerCard
                        ref={activitiesUnifiedRef}
                        dealId={dealxgId}
                        capabilities={{
                            contractValidation: isContractingContext,
                            readOnly: isLocked,
                            enableSpd: isSpdManagementEnabled,
                            enableBusinessConversion: true,
                            // Off while the toggle is off: F304043's interim flow opens the buyer form
                            // on a customer-less quote legitimately.
                            requireBuyer: isNativeAddCustomerEnabled,
                            warnOnBuyerEmailChange: isEcommerceCancelOnBuyerEmailChangeEnabled && isEcommerceDeal
                        }}
                        onCancel={closeCustomerSlideOut}
                        onStateChange={setActivitiesShellState}
                        onSave={handleActivitiesSave}
                        onAnalyticsEvent={handleActivitiesAnalyticsEvent}
                        onBusinessConversionComplete={handleBusinessConversionComplete}
                        hideLoader
                        initialTab={activeCustomerTab as CustomerTabKey | typeof INSURANCE}
                        hostApp="MV"
                    />
                </LoadingSectionParent>
            ) : (
                <UnifiedCustomerCard
                    ref={unifiedRef}
                    buyerBusinessConversionDraft={buyerBusinessConversionDraft}
                    onStateChange={setShellState}
                    onCancelConversion={resetBuyerBusinessConversionDraft}
                />
            )}
        </StyledEditCustomerSlideOut>
    );
};

export default EditCustomerSlideout;
